import React from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { track } from "@vercel/analytics";
import styles from "./Services.module.css";

const CAL_BOOKING_URL = "https://cal.com/triggerflow/30min";

const services = [
  {
    title: "Website Builds",
    outcome: "A clear online presence that makes it easy for customers to find and contact you.",
    body: "We build simple one-page websites through to multi-page business sites, depending on what you need. The approach — whether that is a custom build, a platform like Wix or Squarespace, or something else — depends on your budget, goals and how much you want to manage yourself. Every project includes handover guidance so you know how to maintain what has been built.",
    deliverables: [
      "Live, deployed website",
      "Handover guide and walkthrough",
      "Post-launch check",
    ],
    cta: "Discuss your website",
  },
  {
    title: "Workflow & Business Automation",
    outcome: "Less time spent on repetitive admin, fewer steps done manually.",
    body: "We look at the processes that slow your business down — manual data entry, status updates, internal notifications, follow-ups — and build automations that handle them. This might involve connecting existing tools, building custom integrations, or setting up simple trigger-based workflows. The goal is to reduce the manual steps between systems without making things more complicated.",
    deliverables: [
      "Process review and automation plan",
      "Connected workflows and integrations",
      "Documentation and handover",
    ],
    cta: "Talk through your workflow",
  },
  {
    title: "Ordering & Booking Systems",
    outcome: "A structured digital process for businesses that take orders or bookings.",
    body: "If your business currently takes orders or bookings through informal channels — messages, phone calls, paper forms — we can replace that with a simple digital system built around your actual workflow. This is not an off-the-shelf booking plugin. It is a custom solution designed to match how your business operates, with the right notifications, confirmations and handoff points built in.",
    deliverables: [
      "Web-based ordering or booking interface",
      "Order management workflow",
      "Email and notification setup",
    ],
    cta: "Talk through your process",
  },
  {
    title: "Consultancy & Digital Setup Guidance",
    outcome: "Clarity on what tools and systems are right for your business — before you commit to building anything.",
    body: "Sometimes you do not need someone to build anything yet — you need help working out what to build, what tools to use, and what order to do it in. We offer scoped consultancy sessions to review your current setup, map out your options, and give you a clear recommendation. Good for businesses at an early stage or those who have outgrown their current tools and do not know what to move to next.",
    deliverables: [
      "Discovery session",
      "Written summary of recommendations",
      "Optional follow-up support",
    ],
    cta: "Book a discovery session",
  },
  {
    title: "Forms, Dashboards & Internal Tools",
    outcome: "Better visibility of what is happening in your business, and easier ways to capture information.",
    body: "When a standard form or spreadsheet is not enough, we can build something more structured — a custom form that captures the right data, an internal dashboard that shows live information, or a simple tool your team can use without switching between systems. Designed around how you actually work, not around a generic template.",
    deliverables: [
      "Custom form or dashboard interface",
      "Data connections and live feeds where needed",
      "Access controls and documentation",
    ],
    cta: "Explore what we can build",
  },
  {
    title: "UI/API Test Automation",
    outcome: "More reliable software delivery, with issues caught before they reach your users.",
    body: "This is a specialist service for teams that build software and want more confidence in their releases. We design and build automated test suites that run against your UI and APIs — covering the critical paths that matter most. We also offer standalone QA audits and test strategy reviews for teams that need a clearer testing approach without a full implementation.",
    deliverables: [
      "Test strategy and coverage plan",
      "Automated test suite",
      "CI pipeline integration",
      "Run instructions and reports",
    ],
    cta: "Discuss your testing needs",
  },
  {
    title: "Support & Maintenance",
    outcome: "Your systems keep working after launch.",
    body: "Once a website or system is live, things change — content needs updating, small issues come up, integrations need adjusting. We offer ongoing support to keep things running, whether that is a regular maintenance arrangement or ad-hoc help when something needs fixing. You will not be left managing something that quietly breaks six months after handover.",
    deliverables: [
      "Scheduled maintenance and updates",
      "Bug fix response",
      "Priority email support",
    ],
    cta: "Enquire about support",
  },
];

export default function Services() {
  return (
    <>
      <Helmet>
        <title>Services — TriggerFlow</title>
        <meta name="description" content="Website builds, workflow automation, ordering systems, dashboards and more. Fixed-price services for small businesses — scoped upfront, no hidden retainers." />
      </Helmet>

      <section className={styles.introSection}>
        <div className={styles.introInner}>
          <h1 className="section__title">Services</h1>
          <p className={styles.intro}>
            From simple one-page websites to custom forms, dashboards, automation and testing
            support, TriggerFlow helps businesses choose the right digital setup for their
            budget, goals and stage.
          </p>
          <p className={styles.pricingNote}>
            Every service is quoted at a <strong>fixed price after a free scoping call</strong> — no hourly rates, no hidden retainers.
          </p>
        </div>
      </section>

      <div className={styles.serviceList}>
        {services.map((s, i) => (
          <article
            key={s.title}
            className={`${styles.serviceItem} ${i % 2 === 1 ? styles.serviceItemAlt : ""}`}
          >
            <div className={styles.serviceContent}>
              <div className={styles.serviceHeader}>
                <h2 className={styles.serviceTitle}>{s.title}</h2>
                <p className={styles.serviceOutcome}>{s.outcome}</p>
              </div>
              <p className={styles.serviceBody}>{s.body}</p>
              <div className={styles.serviceFooter}>
                <div className={styles.deliverables}>
                  <p className={styles.deliverablesLabel}>Deliverables</p>
                  <ul>
                    {s.deliverables.map(d => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                </div>
                <NavLink to="/contact" className="btn btn--primary">{s.cta}</NavLink>
              </div>
            </div>
          </article>
        ))}
      </div>

      <section className="section">
        <div className="cta">
          <h2>Not sure which service fits your needs?</h2>
          <p>
            Describe the problem you are trying to solve. We will suggest the most
            practical approach — no jargon, no overselling.
          </p>
          <div className={styles.ctaButtons}>
            <a
              href={CAL_BOOKING_URL}
              className="btn btn--primary"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("booking_click", { location: "services_cta" })}
            >
              Book a Free Call {/* PLACEHOLDER: Cal.com link */}
            </a>
            <NavLink to="/contact" className="btn btn--ghost">Send a Message</NavLink>
          </div>
        </div>
      </section>
    </>
  );
}
